import React, { useState, useEffect, useContext } from 'react';
import { useAppContext } from './AppContext';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid, Typography, 
  IconButton, Container, CssBaseline, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert, Card, Avatar, Box, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { ThemeProvider } from "@mui/material/styles";
import { collection, getDocs, setDoc, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { db, auth } from './firebase'; // Import Firestore and Auth instances
import { createUserWithEmailAndPassword } from "firebase/auth"; // Import createUserWithEmailAndPassword
import theme from "./theme";
import Layout from "./Layout";
import AppContext from './AppContext';

const UserManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: '', phone: '', username: '', password: '', confirmPassword: '', role: ''
  });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const { shopId, t } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(false); 

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const employeeCollection = collection(db, "users");
        const q = query(employeeCollection, where("shopId", "==", shopId));
        const employeeSnapshot = await getDocs(q);
        const employeeList = employeeSnapshot.docs.map(doc => ({
          id: doc.id, // Username will be used as the document ID
          ...doc.data()
        }));
        setEmployees(employeeList);
      } catch (error) {
        console.error("Error fetching employees: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [shopId]);

  const handleOpen = () => {
    setEditMode(false);
    setNewEmployee({ name: '', phone: '', username: '', password: '', confirmPassword: '', role: '' });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee({ ...newEmployee, [name]: value });
  };

  const checkUsernameAvailability = (username) => {
    return !employees.some(employee => employee.username === username);
  };

  const handleSave = async () => {
    if (!editMode && !checkUsernameAvailability(newEmployee.username)) {
      setAlert({ open: true, message: t('Username already taken!'), severity: 'error' });
      return;
    }

    if (newEmployee.password !== newEmployee.confirmPassword) {
      setAlert({ open: true, message: t('Passwords do not match!'), severity: 'error' });
      return;
    }

    const employeeData = {
      ...newEmployee,
      shopId
    };

    try {
      if (editMode) {
        const employeeRef = doc(db, "users", selectedEmployee.username);
        await updateDoc(employeeRef, employeeData);
        setEmployees(employees.map(emp => emp.id === selectedEmployee.username ? { ...selectedEmployee, ...newEmployee } : emp));
      } else {
        // Create a new user in Firebase Authentication
        await createUserWithEmailAndPassword(auth, newEmployee.username, newEmployee.password);

        // Add user data to Firestore
        const employeeRef = doc(db, "users", newEmployee.username); 
        await setDoc(employeeRef, { ...employeeData},newEmployee.username); // Use setDoc to create document with specific ID
        setEmployees([...employees, { ...newEmployee, id: newEmployee.username }]);
      }

      handleClose();
      setAlert({ open: true, message: editMode ? t('Employee updated successfully!') : t('Employee added successfully!'), severity: 'success' });
    } catch (error) {
      console.error("Error saving employee: ", error);
      setAlert({ open: true, message: t('Error saving employee. Please try again.'), severity: 'error' });
    }
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setNewEmployee({ ...employee, password: '', confirmPassword: '' });
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (uid) => {
    try {
      await deleteDoc(doc(db, "users", uid)); // Delete document by UID
      setEmployees(employees.filter(emp => emp.id !== uid));
      setAlert({ open: true, message: t('Employee deleted successfully!'), severity: 'success' });
    } catch (error) {
      console.error("Error deleting employee: ", error);
      setAlert({ open: true, message: t('Error deleting employee. Please try again.'), severity: 'error' });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout>
      <Container>
      {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t("You are offline. Some features may not be available.")}
            </Alert>
          )}
          <Typography variant="h4" mt={3} mb={0}>{t('User Management')}</Typography>
        <IconButton color="primary" onClick={handleOpen} style={{ marginTop: '16px', marginBottom: '5px' }}>
          <Add /><Typography>{t('Add Employee')}</Typography>
        </IconButton>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {employees.map((employee, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card variant="outlined" sx={{ padding: "15px", position: 'relative' }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar style={{ marginRight: '16px' }} />
                    <Typography variant="h6">{employee.name}</Typography>
                  </Box>

                  <Typography variant="body2">{t("Phone")}: {employee.phone}</Typography>
                  <Typography variant="body2">{t("Username")}: {employee.username}</Typography>
                  <Typography variant="body2">{t("Role")}: {employee.role}</Typography>

                  <Box
                    position="absolute"
                    top={15}
                    right={15}
                    display="flex"
                    flexDirection="row"
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(employee)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleDelete(employee.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{editMode ? t('Edit Employee') : t('Add New Employee')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={t('Name')}
              name="name"
              fullWidth
              value={newEmployee.name}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              label={t('Phone')}
              name="phone"
              fullWidth
              value={newEmployee.phone}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              label={t('Username')}
              name="username"
              fullWidth
              value={newEmployee.username}
              onChange={handleChange}
              disabled={editMode}
            />
            <TextField
              margin="dense"
              label={t('Password')}
              name="password"
              type="password"
              fullWidth
              value={newEmployee.password}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              label={t('Confirm Password')}
              name="confirmPassword"
              type="password"
              fullWidth
              value={newEmployee.confirmPassword}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('Role')}</InputLabel>
              <Select
                name="role"
                value={newEmployee.role}
                onChange={handleChange}
                label={t('Role')}
              >
                <MenuItem value="manager">{t('Manager')}</MenuItem>
                <MenuItem value="cashier">{t('Cashier')}</MenuItem>
                <MenuItem value="operator">{t('Operator')}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              {t('Cancel')}
            </Button>
            <Button onClick={handleSave} color="primary">
              {t('Save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={alert.open}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
      </Layout>
    </ThemeProvider>
  );
};

export default UserManagement;
